import { NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  ListItem,
  ListOperation,
  resolveOperationUpgradeIcon,
} from '../scrolling-list';

@Component({
  selector: 'operations-menu',
  templateUrl: './operations-menu.component.html',
  styleUrls: ['./operations-menu.component.scss'],
  imports: [
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    NgClass,
    SharedModule,
  ],
})
export class OperationsMenuComponent<T extends { folderId?: string }> {
  private permissionService = inject(PermissionService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @Input() operations: ListOperation<T>[] = [];
  @Input() entityId?: string;
  @Input() listItem?: ListItem<T>;
  @Input() id: string;

  @Output() operationClick = new EventEmitter<string>();

  @ViewChild('matMenuTrigger') matMenuTrigger: MatMenuTrigger;

  constructor() {
    this.permissionService.shareAccessChanged$
      ?.pipe(takeUntilDestroyed())
      .subscribe(() => this.changeDetectorRef.detectChanges());
  }

  protected handleClick(operationName: string) {
    this.operationClick.emit(operationName);
  }

  protected getUpgradeIcon(operation: ListOperation<T>): string | undefined {
    return resolveOperationUpgradeIcon(operation.upgradeIcon);
  }

  protected getOperationIcon(operation: ListOperation<T>): string {
    if (operation.icon) {
      return operation.icon;
    }

    const iconMap: Record<string, string> = {
      Delete: 'trash',
      Move: 'move-to-folder',
      License: 'license',
      'Remote Commands': 'remote-commands',
      'Display Settings': 'settings',
      'Launch Rise Player': 'play-no-fill',
      'Restart Rise Player': 'restart',
      'Reboot Media Player': 'reboot',
      'Turn On Screen': 'turn-on-screen',
      'Turn Off Screen': 'turn-off-screen',
      'Update Firmware': 'update',
      Export: 'export-all',
      'Export All': 'export-all',
      Copy: 'duplicate',
      'Copy to Sub-company': 'copy-to-sub-company',
      'Copy to sub company': 'copy-to-sub-company',
      'Make a Copy': 'duplicate',
      'Make a copy': 'duplicate',
      'Screen Sharing': 'screen-share',
      'Set Monitoring': 'monitoring',
      'Set Reboot Time': 'reboot-time',
      'Set Address': 'set-address',
      'Enable Display Control': 'enable-display',
      'Disable Display Control': 'disable-display',
      'Move To Company': 'move-display',
      'Move To Trash': 'trash',
      'Assign Schedule': 'calendar',
      Download: 'download',
      Duplicate: 'duplicate',
      'Copy URL': 'copy',
      Rename: 'edit',
      'Control Alert Volume': 'volume',
      'Screen Power On': 'turn-on-screen',
    };

    return iconMap[operation.name] ?? 'alert-triangle';
  }

  protected get singleActions() {
    const visibleOperations = this.operations
      .filter(
        (operation) => !operation.showIn || operation.showIn.includes('single'),
      )
      .filter((operation) =>
        operation.separator ? true : this.meetsAccessRequirement(operation),
      )
      .filter((operation) => operation.separator || this.isVisible(operation));
    const actions: ListOperation<T>[] = [];
    const groups = new Map<string, ListOperation<T>>();

    for (const operation of visibleOperations) {
      if (!operation.singleGroup) {
        if (!operation.separator) {
          actions.push(operation);
        }
        continue;
      }

      let group = groups.get(operation.singleGroup.name);
      if (!group) {
        group = {
          name: operation.singleGroup.name,
          icon: operation.singleGroup.icon,
          singleOrder: operation.singleGroup.order,
          submenu: true,
          children: [],
        } as ListOperation<T>;
        groups.set(operation.singleGroup.name, group);
        actions.push(group);
      }
      group.children?.push(operation);
    }

    return actions
      .filter(
        (operation) =>
          !operation.submenu || this.submenuActions(operation).length > 0,
      )
      .sort(
        (first, second) =>
          (first.singleOrder ?? Number.MAX_SAFE_INTEGER) -
          (second.singleOrder ?? Number.MAX_SAFE_INTEGER),
      );
  }

  protected submenuActions(operation: ListOperation<T>): ListOperation<T>[] {
    const visibleChildren = (operation.children ?? []).filter(
      (child) =>
        child.separator ||
        (this.meetsAccessRequirement(child) && this.isVisible(child)),
    );

    return visibleChildren.filter((child, index) => {
      if (!child.separator) {
        return true;
      }

      return (
        index > 0 &&
        index < visibleChildren.length - 1 &&
        !visibleChildren[index - 1].separator &&
        !visibleChildren[index + 1].separator
      );
    });
  }

  private isVisible(operation: ListOperation<T>): boolean {
    const selection = this.listItem ? [this.listItem] : undefined;

    return (
      !(operation.hidden && operation.hidden(selection)) &&
      !(
        this.listItem &&
        operation.filter &&
        !operation.singleIgnoreFilter &&
        !operation.filter(this.listItem)
      )
    );
  }

  private meetsAccessRequirement(operation: ListOperation<T>): boolean {
    return this.permissionService.meetsAccessRequirement(
      this.entityId ? { ...operation, entityId: this.entityId } : operation,
    );
  }
}
